import React, { useMemo } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { DASH } from "./insight/InsightCharts.js";
import { useLanguage } from "../i18n/LanguageContext.js";

const END_COLORS = {
  fail: "#E74C3C",
  ongoing: "#1ABC9C",
  gap: "#9CA3AF",
  missed: "#D1D5DB",
  partial: "#76D7C4",
};

/** Max height of the scrollable chart — scroll inside to see every past attempt. */
const CHART_SCROLL_MAX_HEIGHT = 340;

/**
 * Horizontal bar chart — length = days (scales to 40+ day streaks).
 * Vertical scroll — newest attempts first, scroll down for full history.
 *
 * @param {{ runs: import('../utils/streakHistory.js').StreakRun[], variant?: 'default' | 'dashboard' }} props
 */
export function StreakHistoryChart({ runs, variant = "default" }) {
  const { t } = useLanguage();
  const END_LABELS = {
    fail: t("streak_reset"),
    ongoing: t("streak_active"),
    gap: t("streak_gap"),
    missed: t("streak_missed"),
    partial: t("streak_partial_label"),
  };
  const dark = variant === "dashboard";
  const s = dark ? dashStyles : styles;
  const maxDays = useMemo(() => Math.max(1, ...runs.map((r) => r.days)), [runs]);
  const bestDays = maxDays;
  const failCount = runs.filter((r) => r.endedHow === "fail").length;

  const displayRuns = useMemo(() => [...runs].reverse(), [runs]);

  if (runs.length === 0) {
    return (
      <View style={s.empty}>
        <Text style={s.emptyText}>No streak runs yet. Mark successes, then a fail to see your history here.</Text>
      </View>
    );
  }

  return (
    <View style={s.wrap}>
      <Text style={s.title}>{t("streak_runs_title")}</Text>
      <Text style={s.sub}>
        {t("streak_runs_sub")}
      </Text>

      <View style={s.summaryRow}>
        <Text style={s.summaryText}>
          {t("streak_longest")} <Text style={s.summaryBold}>{bestDays} {t("home_days").toLowerCase()}</Text>
        </Text>
        <Text style={s.summaryText}>
          {t("streak_attempts_label")} <Text style={s.summaryBold}>{runs.length}</Text>
          {failCount > 0 ? ` · ${failCount} ${t("streak_reset").toLowerCase()}` : ""}
        </Text>
      </View>

      <ScrollView
        style={s.chartScroll}
        contentContainerStyle={s.chartScrollContent}
        nestedScrollEnabled
        showsVerticalScrollIndicator
      >
        {displayRuns.map((run) => {
          const color = END_COLORS[run.endedHow] || "#64748B";
          const widthPct = Math.max(6, Math.round((run.days / maxDays) * 100));
          const isActive = run.endedHow === "ongoing";

          return (
            <View key={run.id} style={s.runBlock}>
              <View style={s.runHeader}>
                <Text style={s.runAttempt}>
                  #{run.attemptNumber}
                  {isActive ? ` · ${t("streak_now")}` : ""}
                </Text>
                <Text style={[s.runStatus, { color }]}>{END_LABELS[run.endedHow] || run.endedHow}</Text>
              </View>

              <View style={s.barRow}>
                <View style={s.barTrack}>
                  <View style={[s.barFill, { width: `${widthPct}%`, backgroundColor: color }]} />
                </View>
                <Text style={s.barDays}>{run.days}d</Text>
              </View>

              <Text style={s.runMeta}>
                {run.startDate} → {run.endDate}
                {run.trigger ? ` · ${run.trigger}` : ""}
              </Text>
            </View>
          );
        })}
      </ScrollView>

      {runs.length > 4 ? (
        <Text style={s.scrollHint}>{t("streak_scroll_hint").replace("{count}", runs.length)}</Text>
      ) : null}

      <View style={s.legend}>
        <LegendDot color={END_COLORS.ongoing} label={t("streak_active_run")} styles={s} />
        <LegendDot color={END_COLORS.fail} label={t("streak_ended_fail")} styles={s} />
        <LegendDot color={END_COLORS.gap} label={t("streak_broken_gap")} styles={s} />
      </View>
    </View>
  );
}

/** @param {{ color: string, label: string, styles: object }} props */
function LegendDot({ color, label, styles: legendStyles }) {
  return (
    <View style={legendStyles.legendItem}>
      <View style={[legendStyles.legendDot, { backgroundColor: color }]} />
      <Text style={legendStyles.legendText}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: 8,
    padding: 14,
    borderRadius: 12,
    backgroundColor: "#F8FAFC",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  title: { fontSize: 16, fontWeight: "800", color: "#0F172A" },
  sub: { fontSize: 12, color: "#64748B", marginTop: 4, marginBottom: 10, lineHeight: 17 },
  summaryRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E2E8F0",
  },
  summaryText: { fontSize: 12, color: "#64748B" },
  summaryBold: { fontWeight: "800", color: "#0F172A" },
  chartScroll: {
    maxHeight: CHART_SCROLL_MAX_HEIGHT,
  },
  chartScrollContent: {
    paddingBottom: 4,
  },
  runBlock: {
    marginBottom: 14,
  },
  runHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  runAttempt: { fontSize: 13, fontWeight: "800", color: "#334155" },
  runStatus: { fontSize: 11, fontWeight: "700" },
  barRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  barTrack: {
    flex: 1,
    height: 22,
    borderRadius: 8,
    backgroundColor: "#E2E8F0",
    overflow: "hidden",
    justifyContent: "center",
  },
  barFill: {
    height: "100%",
    borderRadius: 8,
    minWidth: 8,
  },
  barDays: {
    width: 36,
    fontSize: 13,
    fontWeight: "800",
    color: "#0F172A",
    textAlign: "right",
  },
  runMeta: {
    fontSize: 11,
    color: "#94A3B8",
    marginTop: 4,
  },
  scrollHint: {
    fontSize: 11,
    color: "#64748B",
    textAlign: "center",
    marginTop: 4,
    marginBottom: 8,
    fontStyle: "italic",
  },
  legend: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 8 },
  legendItem: { flexDirection: "row", alignItems: "center", gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: "#64748B" },
  empty: { padding: 16, backgroundColor: "#F8FAFC", borderRadius: 12 },
  emptyText: { fontSize: 13, color: "#64748B", lineHeight: 18, fontStyle: "italic" },
});

const dashStyles = StyleSheet.create({
  wrap: {
    marginTop: 0,
    padding: 14,
    borderRadius: 18,
    backgroundColor: DASH.card,
    borderWidth: 1,
    borderColor: DASH.border,
  },
  title: { fontSize: 12, fontWeight: "800", color: CF.primaryBright, letterSpacing: 0.5, ...DASH.textShadowSoft },
  sub: { fontSize: 11, color: DASH.textMuted, marginTop: 4, marginBottom: 10, lineHeight: 17, fontWeight: "600", ...DASH.textShadowSoft },
  summaryRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: DASH.border,
  },
  summaryText: { fontSize: 12, color: DASH.textMuted, fontWeight: "600", ...DASH.textShadowSoft },
  summaryBold: { fontWeight: "800", color: DASH.text, ...DASH.textShadow },
  chartScroll: { maxHeight: CHART_SCROLL_MAX_HEIGHT },
  chartScrollContent: { paddingBottom: 4 },
  runBlock: { marginBottom: 14 },
  runHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  runAttempt: { fontSize: 13, fontWeight: "800", color: DASH.textSoft, ...DASH.textShadowSoft },
  runStatus: { fontSize: 11, fontWeight: "700", ...DASH.textShadowSoft },
  barRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  barTrack: {
    flex: 1,
    height: 22,
    borderRadius: 8,
    backgroundColor: "rgba(45, 212, 191, 0.22)",
    overflow: "hidden",
    justifyContent: "center",
  },
  barFill: { height: "100%", borderRadius: 8, minWidth: 8 },
  barDays: { width: 36, fontSize: 13, fontWeight: "800", color: DASH.text, textAlign: "right", ...DASH.textShadow },
  runMeta: { fontSize: 11, color: DASH.textMuted, marginTop: 4, fontWeight: "600", ...DASH.textShadowSoft },
  scrollHint: {
    fontSize: 11,
    color: DASH.textSoft,
    textAlign: "center",
    marginTop: 4,
    marginBottom: 8,
    fontStyle: "italic",
    ...DASH.textShadowSoft,
  },
  legend: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 8 },
  legendItem: { flexDirection: "row", alignItems: "center", gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: DASH.textSoft, fontWeight: "600", ...DASH.textShadowSoft },
  empty: {
    padding: 16,
    backgroundColor: DASH.card,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: DASH.border,
  },
  emptyText: { fontSize: 13, color: DASH.textSoft, lineHeight: 18, fontStyle: "italic", ...DASH.textShadowSoft },
});
